import React, { useCallback, useEffect, useState } from 'react';
import { dashboardApi } from '../../services/api';

const PERIODS = [
  { id: 'month', label: 'Mês atual' },
  { id: 'last30d', label: 'Últimos 30 dias' }
];
const STORAGE_KEY = 'dashboardSummaryPeriod';

const readPeriod = () => {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return PERIODS.some(p => p.id === v) ? v : 'month';
  } catch { return 'month'; }
};

const fmtMoney = (v, cur = 'BRL') => {
  try { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: cur }).format(Number(v) || 0); }
  catch { return `${cur} ${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; }
};
const fmtInt = (v) => Math.round(Number(v) || 0).toLocaleString('pt-BR');
const fmtPct = (v) => `${(Number(v) || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`;

const PLATFORMS = {
  meta: { label: 'Meta Ads', sub: 'Facebook & Instagram', icon: 'social_leaderboard', tone: 'text-[#1877f2] bg-[#1877f2]/10' },
  google: { label: 'Google Ads', sub: 'Search & Display', icon: 'search', tone: 'text-amber-600 bg-amber-500/10' }
};

function timeAgo(iso) {
  if (!iso) return null;
  const diffMs = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(diffMs)) return null;
  const min = Math.max(0, Math.floor(diffMs / 60000));
  if (min < 60) return `há ${Math.max(min, 1)} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.floor(h / 24)} dias`;
}

// Variação vs. período anterior; só aparece quando a API devolver previousTotals.
function Delta({ now, prev, inverse = false }) {
  const a = Number(now);
  const b = Number(prev);
  if (prev === undefined || prev === null || !Number.isFinite(a) || !Number.isFinite(b) || b === 0) return null;
  const pct = ((a - b) / Math.abs(b)) * 100;
  if (!Number.isFinite(pct)) return null;
  const good = inverse ? pct <= 0 : pct >= 0;
  return (
    <span className={`inline-flex items-center text-xs font-semibold ${good ? 'text-tertiary' : 'text-error'}`}>
      <span className="material-symbols-outlined text-[14px]" aria-hidden="true">{pct >= 0 ? 'trending_up' : 'trending_down'}</span>
      {pct >= 0 ? '+' : ''}{pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%
      <span className="sr-only"> em relação ao período anterior</span>
    </span>
  );
}

function HeroKpi({ label, value, sub, delta, accent = false }) {
  return (
    <div className={`rounded-xl border p-5 min-w-0 shadow-[0_2px_2px_rgba(86,94,116,0.04)] ${accent ? 'bg-brand text-white border-brand' : 'bg-white border-slate-200'}`}>
      <p className={`text-[11px] font-semibold uppercase tracking-widest mb-2 ${accent ? 'text-white/80' : 'text-slate-600'}`}>{label}</p>
      <p className="font-headline font-bold text-2xl lg:text-3xl truncate tabular-nums" title={String(value)}>{value}</p>
      <div className={`mt-1.5 flex items-center gap-2 text-xs min-h-[1rem] ${accent ? 'text-white/80' : 'text-slate-600'}`}>
        {delta}
        {sub && <span>{sub}</span>}
      </div>
    </div>
  );
}

function MiniStat({ label, value, delta }) {
  return (
    <div className="min-w-0 px-4 py-3">
      <dt className="text-[11px] font-semibold uppercase tracking-widest text-slate-600">{label}</dt>
      <dd className="mt-1 font-mono font-medium text-base text-on-surface truncate tabular-nums" title={String(value)}>{value}</dd>
      {delta && <div className="mt-0.5">{delta}</div>}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4" aria-hidden="true">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[0, 1, 2].map(i => (
          <div key={i} className="bg-white border border-slate-200 p-5 rounded-xl">
            <div className="animate-pulse h-3 w-24 bg-slate-200 rounded mb-4" />
            <div className="animate-pulse h-8 w-36 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
      <div className="animate-pulse h-20 bg-white border border-slate-200 rounded-xl" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[0, 1].map(i => <div key={i} className="animate-pulse h-32 bg-white border border-slate-200 rounded-xl" />)}
      </div>
    </div>
  );
}

function PlatformCard({ id, t, cur, totalSpend }) {
  const meta = PLATFORMS[id] || { label: id, sub: '', icon: 'campaign', tone: 'text-slate-600 bg-slate-100' };
  const share = totalSpend > 0 ? Math.min(100, ((Number(t?.spend) || 0) / totalSpend) * 100) : 0;
  return (
    <article className="bg-white border border-slate-200 rounded-xl p-5 shadow-[0_2px_2px_rgba(86,94,116,0.04)]">
      <header className="flex items-center gap-3 mb-4">
        <span className={`w-9 h-9 rounded-lg flex items-center justify-center ${meta.tone}`}>
          <span className="material-symbols-outlined text-[20px]" aria-hidden="true">{meta.icon}</span>
        </span>
        <div className="min-w-0">
          <h3 className="font-headline font-bold text-sm text-on-surface leading-tight">{meta.label}</h3>
          <p className="text-xs text-slate-600">{meta.sub}</p>
        </div>
        <span className="ml-auto text-xs font-semibold text-brand bg-brand/10 rounded-full px-2.5 py-0.5">{share.toLocaleString('pt-BR', { maximumFractionDigits: 0 })}% do investimento</span>
      </header>
      <dl className="grid grid-cols-3 gap-3">
        <div className="min-w-0"><dt className="text-[11px] uppercase tracking-widest text-slate-600">Investimento</dt><dd className="font-mono font-medium text-sm text-on-surface truncate tabular-nums">{fmtMoney(t?.spend, cur)}</dd></div>
        <div className="min-w-0"><dt className="text-[11px] uppercase tracking-widest text-slate-600">Cliques</dt><dd className="font-mono font-medium text-sm text-on-surface tabular-nums">{fmtInt(t?.clicks)}</dd></div>
        <div className="min-w-0"><dt className="text-[11px] uppercase tracking-widest text-slate-600">Resultados</dt><dd className="font-mono font-medium text-sm text-on-surface tabular-nums">{fmtInt(t?.results)}</dd></div>
      </dl>
      <div className="mt-4 h-1.5 rounded-full bg-slate-100 overflow-hidden" role="presentation">
        <div className="h-full rounded-full bg-brand" style={{ width: `${share}%` }} />
      </div>
    </article>
  );
}

export default function DashboardSummary() {
  const [period, setPeriod] = useState(readPeriod);
  const [state, setState] = useState({ status: 'loading', data: null });

  const load = useCallback(() => {
    let cancelled = false;
    setState(s => ({ status: 'loading', data: s.data }));
    dashboardApi.getSummary(period)
      .then(res => {
        if (cancelled) return;
        const data = res?.data ?? null;
        if (!data) throw new Error('empty');
        setState({ status: 'ok', data });
      })
      .catch(() => { if (!cancelled) setState({ status: 'error', data: null }); });
    return () => { cancelled = true; };
  }, [period]);

  useEffect(() => load(), [load]);

  const choosePeriod = (id) => {
    setPeriod(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch { /* storage indisponivel */ }
  };

  const { status, data } = state;
  const accounts = data?.accounts;
  const ready = !!data && status !== 'error';
  const empty = ready && (!accounts || accounts.totalAllowed === 0 || accounts.withData === 0);
  const cur = data?.primaryCurrency || 'BRL';
  const totals = data?.totals || {};
  const prev = data?.previousTotals || null;
  const otherCurrencies = data?.multiCurrencyWarning
    ? Object.entries(data.byCurrency || {}).filter(([c]) => c !== cur)
    : [];
  const platformIds = ['meta', 'google'].filter(id => data?.byPlatform?.[id]);
  Object.keys(data?.byPlatform || {}).forEach(id => { if (!platformIds.includes(id)) platformIds.push(id); });
  const oldest = data?.updatedAtRange?.oldest;
  const newest = data?.updatedAtRange?.newest;
  const ago = timeAgo(oldest);
  const dl = (key, inverse) => (prev ? <Delta now={totals[key]} prev={prev[key]} inverse={inverse} /> : null);

  return (
    <section aria-labelledby="dash-summary-title" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="dash-summary-title" className="font-headline font-bold text-xl text-on-surface">Resumo consolidado</h2>
        <div className="flex flex-wrap items-center gap-3">
          {ready && !empty && ago && (
            <span
              className="inline-flex items-center gap-1 text-xs text-slate-600"
              title={`Dado mais recente: ${newest ? new Date(newest).toLocaleString('pt-BR') : '—'}. Os dados são atualizados a cada 12 h; não são em tempo real.`}
            >
              <span className="material-symbols-outlined text-[14px]" aria-hidden="true">schedule</span>
              Atualizado {ago}
            </span>
          )}
          <div role="group" aria-label="Período" className="inline-flex rounded-lg bg-slate-100 p-0.5 border border-slate-200 text-xs font-semibold">
            {PERIODS.map(p => (
              <button
                key={p.id}
                type="button"
                aria-pressed={period === p.id}
                onClick={() => choosePeriod(p.id)}
                className={`px-3 py-1.5 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${period === p.id ? 'bg-white text-brand shadow-xs' : 'text-slate-700 hover:text-slate-900'}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div aria-live="polite" aria-busy={status === 'loading'}>
        {status === 'loading' && !data && (
          <>
            <span className="sr-only">Carregando resumo consolidado…</span>
            <Skeleton />
          </>
        )}

        {status === 'error' && (
          <div role="alert" className="rounded-xl bg-error-container border border-error/20 p-5 flex flex-wrap items-center gap-4">
            <span className="material-symbols-outlined text-error" aria-hidden="true">error</span>
            <p className="text-sm text-on-error-container flex-1 min-w-[200px]">Não foi possível carregar o resumo consolidado.</p>
            <button type="button" onClick={load} className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
              Tentar novamente
            </button>
          </div>
        )}

        {empty && (
          <div className="bg-white border border-slate-200 rounded-xl p-8 text-center">
            <span className="material-symbols-outlined text-3xl text-brand mb-2" aria-hidden="true">hourglass_top</span>
            <p className="font-headline font-bold text-lg text-on-surface">
              {accounts?.totalAllowed === 0 ? 'Nenhuma conta disponível para o resumo' : 'Ainda não há dados consolidados'}
            </p>
            {accounts?.totalAllowed > 0 && (
              <p className="text-sm text-slate-600 mt-2 max-w-md mx-auto">
                A primeira sincronização roda em segundo plano. Volte em alguns minutos.
              </p>
            )}
          </div>
        )}

        {ready && !empty && (
          <div className={`space-y-4 transition-opacity ${status === 'loading' ? 'opacity-60' : ''}`}>
            {(accounts?.withoutData > 0 || otherCurrencies.length > 0) && (
              <div className="flex flex-wrap items-start gap-2 text-xs">
                {accounts?.withoutData > 0 && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 text-amber-800 px-3 py-1 font-medium">
                    <span className="material-symbols-outlined text-[14px]" aria-hidden="true">info</span>
                    {accounts.withoutData} {accounts.withoutData === 1 ? 'conta sem dados ainda' : 'contas sem dados ainda'}
                  </span>
                )}
                {otherCurrencies.length > 0 && (
                  <details className="relative">
                    <summary className="list-none cursor-pointer inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-800 px-3 py-1 font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40">
                      <span className="material-symbols-outlined text-[14px]" aria-hidden="true">currency_exchange</span>
                      Totais em {cur} · +{otherCurrencies.length} {otherCurrencies.length === 1 ? 'outra moeda não somada' : 'outras moedas não somadas'}
                    </summary>
                    <div className="mt-2 bg-white border border-slate-200 rounded-xl p-3 overflow-x-auto sm:absolute sm:z-10 sm:shadow-[0_12px_12px_rgba(86,94,116,0.08)]">
                      <table className="text-xs text-slate-800 w-full">
                        <thead>
                          <tr className="text-left text-[11px] uppercase text-slate-600">
                            <th className="pr-4 py-1">Moeda</th><th className="pr-4 py-1">Investimento</th>
                            <th className="pr-4 py-1">Cliques</th><th className="pr-4 py-1">Resultados</th><th className="py-1">Custo/resultado</th>
                          </tr>
                        </thead>
                        <tbody className="font-mono tabular-nums">
                          {otherCurrencies.map(([c, t]) => (
                            <tr key={c} className="border-t border-slate-100">
                              <td className="pr-4 py-1 font-bold">{c}</td>
                              <td className="pr-4 py-1">{fmtMoney(t.spend, c)}</td>
                              <td className="pr-4 py-1">{fmtInt(t.clicks)}</td>
                              <td className="pr-4 py-1">{fmtInt(t.results)}</td>
                              <td className="py-1">{fmtMoney(t.costPerResult, c)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <HeroKpi accent label="Investimento" value={fmtMoney(totals.spend, cur)} delta={dl('spend')} />
              <HeroKpi
                label="Resultados"
                value={fmtInt(totals.results)}
                delta={dl('results')}
                sub={`${fmtInt(totals.conversations)} conversas · ${fmtInt(totals.leads)} leads`}
              />
              <HeroKpi label="Custo por resultado" value={fmtMoney(totals.costPerResult, cur)} delta={dl('costPerResult', true)} />
            </div>

            <dl className="bg-white border border-slate-200 rounded-xl grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 divide-slate-100 divide-x divide-y xl:divide-y-0 shadow-[0_2px_2px_rgba(86,94,116,0.04)]">
              <MiniStat label="Impressões" value={fmtInt(totals.impressions)} delta={dl('impressions')} />
              <MiniStat label="Alcance" value={fmtInt(totals.reach)} delta={dl('reach')} />
              <MiniStat label="Cliques" value={fmtInt(totals.clicks)} delta={dl('clicks')} />
              <MiniStat label="CTR" value={fmtPct(totals.ctr)} delta={dl('ctr')} />
              <MiniStat label="CPC" value={fmtMoney(totals.cpc, cur)} delta={dl('cpc', true)} />
              <MiniStat label="CPM" value={fmtMoney(totals.cpm, cur)} delta={dl('cpm', true)} />
            </dl>

            {platformIds.length > 0 && (
              <div>
                <h3 className="text-[11px] font-semibold uppercase tracking-widest text-slate-600 mb-2">Por plataforma</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {platformIds.map(id => (
                    <PlatformCard key={id} id={id} t={data.byPlatform[id]} cur={cur} totalSpend={Number(totals.spend) || 0} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
